import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ROICalculator = ({ service }) => {
  const [inputs, setInputs] = useState({
    teamSize: '',
    avgSalary: '',
    currentVelocity: '',
    timeToMarket: '',
    defectRate: '',
    deploymentFreq: ''
  });
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const teamSizeOptions = [
    { value: '5', label: '5 engineers' },
    { value: '10', label: '10 engineers' },
    { value: '15', label: '15 engineers' },
    { value: '25', label: '25 engineers' },
    { value: '50', label: '50 engineers' },
    { value: '100', label: '100+ engineers' }
  ];

  const salaryOptions = [
    { value: '80000', label: '$80,000' },
    { value: '100000', label: '$100,000' },
    { value: '120000', label: '$120,000' },
    { value: '150000', label: '$150,000' },
    { value: '180000', label: '$180,000' }
  ];

  const velocityOptions = [
    { value: '30', label: '30% (Poor)' },
    { value: '50', label: '50% (Below Average)' },
    { value: '70', label: '70% (Average)' },
    { value: '85', label: '85% (Good)' },
    { value: '95', label: '95% (Excellent)' }
  ];

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const calculateROI = async () => {
    if (!inputs.teamSize || !inputs.avgSalary || !inputs.currentVelocity) return;

    setIsCalculating(true);
    
    // Simulate calculation
    await new Promise(resolve => setTimeout(resolve, 1500));

    const teamSize = parseInt(inputs.teamSize);
    const avgSalary = parseInt(inputs.avgSalary);
    const currentVelocity = parseInt(inputs.currentVelocity);
    
    // Calculate current costs
    const annualTeamCost = teamSize * avgSalary;
    const currentEfficiency = currentVelocity / 100;
    const wastedCost = annualTeamCost * (1 - currentEfficiency);

    // Calculate improvements based on service type
    let improvements = {};
    
    if (service?.id === 'velocity-engineering') {
      improvements = {
        velocityIncrease: 35,
        defectReduction: 60,
        deploymentIncrease: 300,
        timeToMarketReduction: 40
      };
    } else if (service?.id === 'ownership-culture') {
      improvements = {
        velocityIncrease: 25,
        defectReduction: 45,
        productivityIncrease: 30,
        retentionImprovement: 20
      };
    } else if (service?.id === 'architectural-modernization') {
      improvements = {
        velocityIncrease: 40,
        maintenanceReduction: 50,
        scalabilityImprovement: 200,
        performanceIncrease: 150
      };
    }

    // Calculate savings
    const newVelocity = Math.min(95, currentVelocity + improvements.velocityIncrease);
    const newEfficiency = newVelocity / 100;
    const newWastedCost = annualTeamCost * (1 - newEfficiency);
    const annualSavings = wastedCost - newWastedCost;
    
    // Estimate engagement cost (simplified)
    const engagementCost = teamSize * 15000; // $15k per engineer
    const paybackPeriod = engagementCost / (annualSavings / 12);
    const threeYearROI = ((annualSavings * 3 - engagementCost) / engagementCost) * 100;

    setResults({
      currentCosts: {
        annualTeamCost,
        wastedCost,
        currentEfficiency: currentVelocity
      },
      improvements,
      projectedSavings: {
        annualSavings,
        newEfficiency: newVelocity,
        engagementCost,
        paybackPeriod,
        threeYearROI
      }
    });

    setIsCalculating(false);
  };

  const canCalculate = inputs.teamSize && inputs.avgSalary && inputs.currentVelocity;

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
          <Icon name="Calculator" size={20} className="text-success" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-text-primary">ROI Calculator</h3>
          <p className="text-sm text-text-secondary">
            Calculate potential return on investment for {service?.title || 'our services'}
          </p>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Team Size"
            options={teamSizeOptions}
            value={inputs.teamSize}
            onChange={(value) => handleInputChange('teamSize', value)}
            placeholder="Select team size..."
          />

          <Select
            label="Average Engineer Salary"
            options={salaryOptions}
            value={inputs.avgSalary}
            onChange={(value) => handleInputChange('avgSalary', value)}
            placeholder="Select average salary..."
          />
        </div>

        <Select
          label="Current Sprint Goal Achievement"
          description="What percentage of sprint goals does your team typically achieve?"
          options={velocityOptions}
          value={inputs.currentVelocity}
          onChange={(value) => handleInputChange('currentVelocity', value)}
          placeholder="Select current velocity..."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Time to Market (weeks)"
            type="number"
            placeholder="e.g., 12"
            value={inputs.timeToMarket}
            onChange={(e) => handleInputChange('timeToMarket', e.target.value)}
            description="Average time from idea to production"
          />

          <Input
            label="Defect Rate (%)"
            type="number"
            placeholder="e.g., 15"
            value={inputs.defectRate}
            onChange={(e) => handleInputChange('defectRate', e.target.value)}
            description="Percentage of releases with critical bugs"
          />
        </div>
      </div>

      <Button
        variant="default"
        size="lg"
        fullWidth
        onClick={calculateROI}
        disabled={!canCalculate}
        loading={isCalculating}
        iconName="TrendingUp"
        iconPosition="left"
        className="bg-success hover:bg-success/90 mb-6"
      >
        {isCalculating ? 'Calculating ROI...' : 'Calculate ROI'}
      </Button>

      {results && (
        <div className="space-y-6">
          <div className="border-t border-border pt-6">
            <h4 className="font-semibold text-text-primary mb-4">Current State Analysis</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="DollarSign" size={16} className="text-text-secondary" />
                  <span className="text-sm font-medium text-text-primary">Annual Team Cost</span>
                </div>
                <p className="text-lg font-semibold text-text-primary">
                  ${results.currentCosts.annualTeamCost.toLocaleString()}
                </p>
              </div>
              
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="AlertTriangle" size={16} className="text-warning" />
                  <span className="text-sm font-medium text-text-primary">Wasted Cost</span>
                </div>
                <p className="text-lg font-semibold text-warning">
                  ${results.currentCosts.wastedCost.toLocaleString()}
                </p>
              </div>
              
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Activity" size={16} className="text-text-secondary" />
                  <span className="text-sm font-medium text-text-primary">Efficiency</span>
                </div>
                <p className="text-lg font-semibold text-text-primary">
                  {results.currentCosts.currentEfficiency}%
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-text-primary mb-4">Projected Improvements</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(results.improvements).map(([key, value]) => (
                <div key={key} className="bg-success/5 border border-success/20 rounded-lg p-3">
                  <p className="text-xs text-text-secondary mb-1 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </p>
                  <p className="text-sm font-semibold text-success">
                    +{value}{key.includes('Increase') || key.includes('Improvement') ? '%' : key.includes('Reduction') ? '% less' : '%'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-text-primary mb-4">Financial Impact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-success/5 border border-success/20 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="TrendingUp" size={16} className="text-success" />
                  <span className="text-sm font-medium text-text-primary">Annual Savings</span>
                </div>
                <p className="text-2xl font-bold text-success">
                  ${results.projectedSavings.annualSavings.toLocaleString()}
                </p>
              </div>
              
              <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Clock" size={16} className="text-accent" />
                  <span className="text-sm font-medium text-text-primary">Payback Period</span>
                </div>
                <p className="text-2xl font-bold text-accent">
                  {results.projectedSavings.paybackPeriod.toFixed(1)} months
                </p>
              </div>
            </div>
            
            <div className="mt-4 bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Target" size={16} className="text-primary" />
                <span className="text-sm font-medium text-text-primary">3-Year ROI</span>
              </div>
              <p className="text-3xl font-bold text-primary">
                {results.projectedSavings.threeYearROI.toFixed(0)}%
              </p>
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-text-primary mb-1">Calculation Notes</h4>
                <p className="text-sm text-text-secondary">
                  This ROI calculation is based on industry benchmarks and typical improvements seen in similar engagements. 
                  Actual results may vary based on your specific situation, team dynamics, and implementation approach.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ROICalculator;